<template>
    <div class="container">
        <o-bread-crumb>
            <template v-slot:pageName>Branch Products</template>
            <template v-slot:pageDetail>List</template>
        </o-bread-crumb>

        <div v-if="validation">
            <div class="alert alert-danger mt-1" role="alert">
                {{ validation }}
            </div>
        </div>

        <!-- Feature Disabled State -->
        <div v-if="!isBranchProductEnabled" class="feature-disabled-container mt-4">
            <div class="disabled-content">
                <i class="fas fa-toggle-off fa-3x primary-text mb-3"></i>
                <h4>Product Management Disabled</h4>
                <p>The Product Management feature has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
            </div>
        </div>

        <template v-if="isBranchProductEnabled">
        <div class="card bg-white shadow-none">
            <div class="card-header">
                <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
                    <li class="nav-item">
                        <div class="d-flex d-inline-block">
                            <h4 class="header-title">
                                <i class="fas fa-box-open me-2 text-primary"></i>Product Management
                            </h4>
                        </div>
                        <p class="header-subtitle me-4 mt-1">Manage products across merchants</p>
                    </li>
                </ul>
            </div>

            <div class="card-body">


                <div class="d-flex justify-content-center spinner-border text-primary spinner-position" v-if="page.isLoading"></div>
                <div v-if="page.output">
                    <!-- Filter & Action Bar -->
                    <div class="filter-action-wrapper mb-4 mt-3">
                        <div class="filter-controls">
                            <!-- Search -->
                            <div class="search-box-custom">
                                <div class="search-type-tabs">
                                    <button class="tab-btn active">Product / Merchant</button>
                                </div>
                                <div class="search-input-wrapper">
                                    <input
                                        type="text"
                                        class="form-control-search"
                                        v-model="searchQuery"
                                        placeholder="Search..."
                                        @input="handleSearchInput"
                                    />
                                    <button v-if="!isSearchActive" class="btn-search" @click="handleSearchInput">
                                        <i class="fas fa-search"></i>
                                    </button>
                                    <button v-else class="btn-search" @click="clearSearch">
                                        <i class="fas fa-times text-danger"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="action-section">
                            <div class="action-buttons-group">
                                <button class="btn btn-refresh" @click.prevent="loadData" title="Refresh">
                                    <i class="fas fa-sync-alt"></i>
                                </button>
                                <button v-if="write" class="btn btn-add" @click="openAddModal(null)">
                                    <i class="fas fa-plus me-1"></i> Add Product
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Products Table grouped by Merchant -->
                    <!-- 2x2 Card Grid -->
                    <div class="merchant-cards-grid">
                        <div
                            v-for="merchant in paginatedMerchants"
                            :key="merchant.id"
                            class="merchant-card"
                        >
                            <!-- Card Header -->
                            <div class="mc-header">
                                <div class="mc-icon">
                                    <i class="fas fa-store"></i>
                                </div>
                                <div class="mc-title-block">
                                    <h6 class="mc-title">{{ merchant.name || merchant.business_profile?.name || merchant.id }}</h6>
                                    <span class="mc-badge">{{ getProductsByMerchant(merchant.id).length }} product{{ getProductsByMerchant(merchant.id).length !== 1 ? 's' : '' }}</span>
                                </div>
                            </div>

                            <!-- Card Body: Product List -->
                            <div class="mc-body">
                                <div v-if="getProductsByMerchant(merchant.id).length === 0" class="mc-empty">
                                    <i class="fas fa-box-open fa-2x mb-2"></i>
                                    <p class="mb-0">No products yet</p>
                                </div>

                                <ul v-else class="mc-product-list">
                                    <li
                                        v-for="(product, index) in getDisplayProducts(merchant.id)"
                                        :key="product.id"
                                        class="mc-product-item"
                                    >
                                        <div class="mc-product-info">
                                            <span class="mc-product-no">{{ index + 1 + ((merchantPageMap[merchant.id] || 1) - 1) * productsPerPage }}.</span>
                                            <span class="mc-product-name" :title="product.name">{{ product.name }}</span>
                                            <span class="mc-product-price">{{ formatPrice(product.price) }}</span>
                                        </div>
                                        <div v-if="write" class="mc-product-actions">
                                            <button
                                                class="btn-icon btn-icon-edit"
                                                @click.prevent="openEditModal(product)"
                                                title="Edit"
                                            >
                                                <i class="fas fa-edit"></i>
                                            </button>
                                            <button
                                                class="btn-icon btn-icon-delete"
                                                @click.prevent="confirmDeleteProduct(product)"
                                                title="Delete"
                                            >
                                                <i class="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                    </li>
                                </ul>

                                <!-- Show all / less toggle & Pagination -->
                                <div v-if="getProductsByMerchant(merchant.id).length > 5" class="mc-toggle">
                                    <div v-if="!showAllMap[merchant.id]">
                                        <button class="btn-show-all" @click="toggleShowAll(merchant.id)">
                                            Show all {{ getProductsByMerchant(merchant.id).length }} <i class="fas fa-chevron-down ms-1"></i>
                                        </button>
                                    </div>
                                    <div v-else class="mc-pagination-wrapper">
                                        <div class="mc-pagination">
                                            <button 
                                                class="btn-page" 
                                                @click="prevProductPage(merchant.id)"
                                                :disabled="(merchantPageMap[merchant.id] || 1) === 1"
                                            >
                                                ◄ Previous
                                            </button>
                                            <span class="page-info">
                                                Page {{ merchantPageMap[merchant.id] || 1 }} of {{ getTotalProductPages(merchant.id) }}
                                            </span>
                                            <button 
                                                class="btn-page" 
                                                @click="nextProductPage(merchant.id)"
                                                :disabled="(merchantPageMap[merchant.id] || 1) === getTotalProductPages(merchant.id)"
                                            >
                                                Next ►
                                            </button>
                                        </div>
                                        <button class="btn-show-less" @click="toggleShowAll(merchant.id)">
                                            Show less <i class="fas fa-chevron-up ms-1"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Card Footer -->
                            <div class="mc-footer">
                                <button
                                    v-if="write"
                                    class="btn-add-product"
                                    @click="openAddModal(merchant.id)"
                                >
                                    <i class="fas fa-plus me-1"></i> Add Product
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-if="!page.isLoading && paginatedMerchants.length === 0" class="mc-no-data">
                        <i class="fas fa-inbox fa-3x mb-3"></i>
                        <h5>No merchants found</h5>
                        <p class="text-muted">Try adjusting your search.</p>
                    </div>


                </div>
            </div>

            <!-- Pagination -->
            <div class="container mt-3 mb-3" v-if="totalPages > 1">
                <o-pagination :page="currentPage" :total="totalPages" @callback="changePage" />
            </div>
        </div>

        <!-- Product Form Modal -->
        <product-form-modal
            :isVisible="modalVisible"
            :merchantId="selectedMerchantId"
            :productId="selectedProductId"
            :merchants="allMerchants"
            @close="closeModal"
            @save="handleSave"
        />

        <!-- Delete Confirm -->
        <o-warning-box
            v-if="showDeleteModal"
            title="Delete Product"
            :item-name="selectedProduct?.name || 'this product'"
            warning-message="This action cannot be undone."
            confirm-text="Yes, Delete"
            cancel-text="Cancel"
            @confirm="confirmDelete"
            @cancel="showDeleteModal = false"
        />
        </template>
    </div>
</template>

<script>
import { computed, onMounted, ref, reactive } from 'vue';
import { useStore } from 'vuex';
import { OBreadCrumb, OPagination, OWarningBox } from '@/components/OmnihControl';
import ProductFormModal from '@/components/product/ProductFormModal.vue';
import { useToast } from '@/composables/useToast';

export default {
    name: 'BranchProduct',
    components: {
        OBreadCrumb,
        OPagination,
        OWarningBox,
        ProductFormModal
    },

    setup() {
        const store = useStore();
        const { showToast } = useToast();

        const isBranchProductEnabled = computed(() => {
            return (store.state.FeatureFlag?.branchProductManagement?.flag || 'ACTIVE') === 'ACTIVE'
        });

        const page = reactive({
            isLoading: false,
            output: false
        });

        const allMerchants = ref([]);
        const merchantProducts = ref({});
        const currentPage = ref(1);
        const merchantsPerPage = 4;
        const searchQuery = ref('');
        const isSearchActive = ref(false);
        const validation = ref('');
        const showAllMap = reactive({});
        const merchantPageMap = reactive({});
        const productsPerPage = 10;

        // Modal state
        const modalVisible = ref(false);
        const selectedMerchantId = ref(null);
        const selectedProductId = ref(null);

        // Delete state
        const showDeleteModal = ref(false);
        const selectedProduct = ref(null);

        // Computed
        const userInfo = computed(() => store.state.Auth.userInfo);
        const write = computed(() => {
            const modules = userInfo.value?.modules || [];
            const productModule = modules.find(m => m.module_name === 'product');
            const selectedProfile = store.state.Auth.selectedBusinessProfile;
            const isInBranchContext = (
                selectedProfile?.corporate_id != null && selectedProfile?.corporate_id !== '' &&
                selectedProfile?.branch_id != null && selectedProfile?.branch_id !== '' &&
                (selectedProfile?.merchant_id == null || selectedProfile?.merchant_id === '')
            );
            const level = store.state.Auth.currUser?.level || localStorage.getItem('level');
            return productModule?.write === 1 || level === 'root' || isInBranchContext;
        });

        const filteredMerchants = computed(() => {
            if (!searchQuery.value) return allMerchants.value;
            const query = searchQuery.value.toLowerCase();
            return allMerchants.value.filter(merchant => {
                const merchantName = (merchant.name || merchant.business_profile?.name || '').toLowerCase();
                const hasMatchingProduct = merchantProducts.value[merchant.id]?.some(
                    product => product.name.toLowerCase().includes(query)
                );
                return merchantName.includes(query) || hasMatchingProduct;
            });
        });

        const paginatedMerchants = computed(() => {
            const start = (currentPage.value - 1) * merchantsPerPage;
            return filteredMerchants.value.slice(start, start + merchantsPerPage);
        });

        const totalPages = computed(() =>
            Math.ceil(filteredMerchants.value.length / merchantsPerPage)
        );

        // Methods
        const loadMerchants = async () => {
            let branchId = userInfo.value.business_profile?.branch_id;
            if (!branchId) {
                try {
                    const profile = await store.dispatch('Auth/getBusinessProfile');
                    branchId = profile.branch_id;
                } catch (e) {
                    validation.value = 'Failed to load user profile';
                    return;
                }
            }
            if (!branchId) {
                validation.value = 'No branch ID found for this user';
                return;
            }
            try {
                const response = await store.dispatch('Merchant/read_all_branch', branchId);
                allMerchants.value = response;
            } catch {
                validation.value = 'Failed to load merchants';
            }
        };

        const loadProductsForMerchant = async (merchantId) => {
            try {
                // Use the dedicated 'all' action to fetch all products for the merchant
                await store.dispatch('Product/all', merchantId);
                merchantProducts.value[merchantId] = [...store.state.Product.products];
            } catch {
                merchantProducts.value[merchantId] = [];
            }
        };

        const loadAllProducts = async () => {
            page.isLoading = true;
            try {
                await loadMerchants();
                for (const merchant of allMerchants.value) {
                    await loadProductsForMerchant(merchant.id);
                }
                page.output = true;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch {
                validation.value = 'Failed to load data';
            } finally {
                page.isLoading = false;
            }
        };

        const loadData = () => loadAllProducts();

        const getProductsByMerchant = (merchantId) => {
            const products = merchantProducts.value[merchantId] || [];
            if (searchQuery.value) {
                const query = searchQuery.value.toLowerCase();
                return products.filter(p => p.name.toLowerCase().includes(query));
            }
            return products;
        };

        const getDisplayProducts = (merchantId) => {
            const products = getProductsByMerchant(merchantId);
            
            if (searchQuery.value) {
                return products;
            }

            if (!showAllMap[merchantId]) {
                return products.slice(0, 5);
            }
            
            const page = merchantPageMap[merchantId] || 1;
            const start = (page - 1) * productsPerPage;
            return products.slice(start, start + productsPerPage);
        };

        const toggleShowAll = (merchantId) => {
            showAllMap[merchantId] = !showAllMap[merchantId];
            if (!showAllMap[merchantId]) {
                merchantPageMap[merchantId] = 1;
            }
        };

        const getTotalProductPages = (merchantId) => {
            const products = getProductsByMerchant(merchantId);
            return Math.ceil(products.length / productsPerPage);
        };

        const nextProductPage = (merchantId) => {
            const total = getTotalProductPages(merchantId);
            if ((merchantPageMap[merchantId] || 1) < total) {
                merchantPageMap[merchantId] = (merchantPageMap[merchantId] || 1) + 1;
            }
        };

        const prevProductPage = (merchantId) => {
            if ((merchantPageMap[merchantId] || 1) > 1) {
                merchantPageMap[merchantId] = (merchantPageMap[merchantId] || 1) - 1;
            }
        };

        const handleSearchInput = () => {
            isSearchActive.value = !!searchQuery.value;
            currentPage.value = 1;
        };

        const clearSearch = () => {
            searchQuery.value = '';
            isSearchActive.value = false;
            currentPage.value = 1;
        };

        const changePage = (p) => {
            currentPage.value = p;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        // Modal handlers
        const openAddModal = (merchantId) => {
            selectedMerchantId.value = merchantId;
            selectedProductId.value = null;
            modalVisible.value = true;
        };

        const openEditModal = (product) => {
            selectedMerchantId.value = product.merchant_id;
            selectedProductId.value = product.id;
            modalVisible.value = true;
        };

        const closeModal = () => {
            modalVisible.value = false;
            selectedMerchantId.value = null;
            selectedProductId.value = null;
        };

        const handleSave = async (merchantId, isEditMode) => {
            showToast({
                type: 'success',
                title: 'Success',
                message: `Product has been ${isEditMode ? 'updated' : 'added'} successfully.`
            });
            await loadAllProducts();
        };

        const confirmDeleteProduct = (product) => {
            selectedProduct.value = product;
            showDeleteModal.value = true;
        };

        const confirmDelete = async () => {
            if (!selectedProduct.value) return;
            const merchantId = selectedProduct.value.merchant_id;
            showDeleteModal.value = false;
            page.isLoading = true;
            try {
                await store.dispatch('Product/delete', selectedProduct.value.id);
                showToast({
                    type: 'success',
                    title: 'Deleted',
                    message: `Product has been deleted successfully.`
                });
                await loadAllProducts();

    
                const productsAfterDelete = getProductsByMerchant(merchantId);
                const newTotalPages = Math.ceil(productsAfterDelete.length / productsPerPage);
                const currentMerchantPage = merchantPageMap[merchantId] || 1;

                if (currentMerchantPage > newTotalPages && newTotalPages > 0) {
                    merchantPageMap[merchantId] = newTotalPages;
                }
            } catch {
                showToast({
                    type: 'error',
                    title: 'Delete Failed',
                    message: 'Failed to delete product'
                });
                validation.value = 'Failed to delete product';
            } finally {
                page.isLoading = false;
                selectedProduct.value = null;
            }
        };

        const formatPrice = (price) =>
            new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(price);

        onMounted(() => loadData());

        return {
            page,
            validation,
            write,
            allMerchants,
            paginatedMerchants,
            currentPage,
            totalPages,
            searchQuery,
            isSearchActive,
            showAllMap,
            modalVisible,
            selectedMerchantId,
            selectedProductId,
            showDeleteModal,
            selectedProduct,
            loadData,
            getProductsByMerchant,
            getDisplayProducts,
            toggleShowAll,
            handleSearchInput,
            clearSearch,
            changePage,
            openAddModal,
            openEditModal,
            closeModal,
            handleSave,
            confirmDeleteProduct,
            confirmDelete,
            formatPrice,
            merchantPageMap,
            productsPerPage,
            getTotalProductPages,
            nextProductPage,
            prevProductPage,
            isBranchProductEnabled
        };
    }
};
</script>

<style scoped>
/* Filter and Action Bar */
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* Search Box */
.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 1 350px;
    min-width: 250px;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--primary-color, #0d6efd);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-weight: 600;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 4px;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
}

.form-control-search {
    border: none;
    padding: 8px 12px;
    font-size: 14px;
    width: 100%;
    outline: none;
    background: transparent;
    color: #334155;
}

.btn-search {
    background: #f8fafc;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search:hover {
    background: #e2e8f0;
    color: var(--primary-color, #0d6efd);
}

/* Action Buttons */
.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.btn-refresh {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border-radius: 8px;
}

.btn-refresh:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

.btn-add {
    background-color: var(--primary-color, #0d6efd);
    color: white;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 8px;
    transition: all 0.2s;
    text-decoration: none;
    cursor: pointer;
}

.btn-add:hover {
    filter: brightness(0.9);
    color: white;
}

/* ── Merchant Card Grid (2x2) ────────────────────────── */
.merchant-cards-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
}

@media (max-width: 767px) {
    .merchant-cards-grid {
        grid-template-columns: 1fr;
    }
}

/* Individual Card */
.merchant-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.merchant-card:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
    transform: translateY(-2px);
}

/* Card Header */
.mc-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
}

.mc-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--primary-color, #0d6efd);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
}

.mc-title-block {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.mc-title {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mc-badge {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
}

/* Card Body */
.mc-body {
    flex: 1;
    padding: 0;
}

.mc-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.5rem 1rem;
    color: #9ca3af;
    font-size: 13px;
    text-align: center;
}

/* Product List */
.mc-product-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.mc-product-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 16px;
    border-bottom: 1px solid #f3f4f6;
    gap: 8px;
    transition: background 0.15s;
}

.mc-product-item:last-child {
    border-bottom: none;
}

.mc-product-item:hover {
    background: #f8fafc;
}

.mc-product-info {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
}

.mc-product-no {
    font-size: 12px;
    color: #9ca3af;
    font-weight: 500;
    flex-shrink: 0;
    min-width: 18px;
}

.mc-product-name {
    font-size: 13px;
    font-weight: 500;
    color: #1f2937;
    white-space: normal;
    word-break: break-word;
    overflow: visible;
    text-overflow: clip;
    flex: 1;
}

.mc-product-price {
    font-size: 12px;
    color: #10b981;
    font-weight: 600;
    flex-shrink: 0;
    white-space: nowrap;
}

.mc-product-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
}

/* Icon-only action buttons */
.btn-icon {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-icon-edit {
    background: #f3f4f6;
    color: #374151;
}

.btn-icon-edit:hover {
    background: #e5e7eb;
    color: #111827;
}

.btn-icon-delete {
    background: #fff1f2;
    color: #9f1239;
}

.btn-icon-delete:hover {
    background: #ffe4e6;
    color: #be123c;
}

/* Show all toggle */
.mc-toggle {
    text-align: left;
    padding: 8px;
    border-top: 1px solid #f3f4f6;
}

.btn-show-all {
    background: transparent;
    border: 1px solid #e2e8f0;
    color: #64748b;
    font-size: 12px;
    padding: 4px 14px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
}

.mc-pagination-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.mc-pagination {
    display: flex;
    align-items: center;
    gap: 8px;
}

.btn-page {
    background: transparent;
    border: none;
    color: var(--primary-color, #0d6efd);
    font-size: 13px;
    cursor: pointer;
    padding: 4px 8px;
    font-weight: 500;
}

.btn-page:disabled {
    color: #cbd5e1;
    cursor: not-allowed;
}

.page-info {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
}

.btn-show-less {
    background: transparent;
    border: none;
    color: #64748b;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 4px;
}

.btn-show-less:hover {
    color: #1e293b;
}

.btn-show-all:hover {
    background: #f1f5f9;
    color: var(--primary-color, #0d6efd);
}

/* Card Footer */
.mc-footer {
    padding: 10px 16px;
    border-top: 1px solid #e5e7eb;
    background: #fafafa;
}

.btn-add-product {
    width: 100%;
    background: var(--primary-color, #0d6efd);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 7px 0;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.btn-add-product:hover {
    filter: brightness(0.9);
}

/* No data state */
.mc-no-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 4rem 1rem;
    color: #9ca3af;
}

.mc-no-data h5 {
    color: #374151;
    margin-top: 0.75rem;
}

/* Header */
.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}



/* Responsive */
@media (max-width: 991px) {
    .filter-controls {
        flex: 100%;
        flex-direction: column;
        align-items: stretch;
    }
    .search-box-custom {
        min-width: 100%;
    }
    .action-buttons-group {
        flex: 100%;
        margin-top: 12px;
    }
    .btn-add {
        flex: 1;
        justify-content: center;
    }
}

/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}
</style>
